import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  inject
} from '@angular/core';
import { Router } from '@angular/router';
import { FilterModel } from 'ag-grid-enterprise';
import { Subscription, forkJoin } from 'rxjs';
import { User } from 'src/app/classes/models';
import { UserAccountRoles } from 'src/app/interfaces/user-account-roles';
import { WidgetPanelCounts } from 'src/app/interfaces/widget-panel-counts';
import { CompanyService } from 'src/app/services/company.service';
import { SharedDataService } from 'src/app/services/shared-data.service';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';

// Consider creating new widget for Client managers.
@Component({
  selector: 'app-contracts-widget',
  templateUrl: './contracts-widget.component.html',
  styleUrls: ['./contracts-widget.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ContractsWidgetComponent implements OnInit {
  private _cdr = inject(ChangeDetectorRef);

  constructor(
    private router: Router,
    private companyService: CompanyService,
    private UtilityService: UtilityService,
    private SharedDataService: SharedDataService,
    private ToastService: ToastService
  ) {}

  @Input() CompanyId!: number;

  contractCounts: Map<
    string,
    {
      count: number;
      query?: {} | undefined;
      route?: string | undefined;
      FilterModel?: FilterModel;
    }
  > = new Map();

  widgetPanelCounts: WidgetPanelCounts = {
    title: 'Contracts',
    noDataMessage: 'You do not have any contracts.',
    hasData: false,
    data: this.contractCounts
  };

  userAccountRole?: UserAccountRoles;
  SharedDataServiceSubscription?: Subscription;

  navigateToContractsPage() {
    this.router.navigate(['client', this.CompanyId, 'contracts']);
  }

  private loadCompanyContracts(CompanyId: number) {
    const contractsRoute = `client/${CompanyId}/contracts`;
    const getContractsQuery = {};

    const draftContractsQuery = {
      ...getContractsQuery,
      status: 'NEW',
      isFinalized: false
    };

    const activeContractsQuery = {
      ...getContractsQuery,
      $or: [
        {
          $and: [{ status: 'NEW' }, { isFinalized: true }]
        },
        {
          status: {
            $in: ['EXPIRES30', 'EXPIRES90', 'RENEW30', 'RENEW90', 'REVIEW']
          }
        }
      ]
    };

    const renewedContractsQuery = {
      ...getContractsQuery,
      status: 'RENEWED'
    };

    const expiredContractsQuery = {
      ...getContractsQuery,
      status: 'EXPIRED'
    };

    const archivedContractsQuery = {
      ...getContractsQuery,
      status: 'ARCHIVED'
    };

    forkJoin([
      this.companyService.getContractCount(
        this.UtilityService.stringifyObjectsInQueryObject(draftContractsQuery)
      ),
      this.companyService.getContractCount(
        this.UtilityService.stringifyObjectsInQueryObject(activeContractsQuery)
      ),
      this.companyService.getContractCount(
        this.UtilityService.stringifyObjectsInQueryObject(renewedContractsQuery)
      ),
      this.companyService.getContractCount(
        this.UtilityService.stringifyObjectsInQueryObject(expiredContractsQuery)
      ),
      this.companyService.getContractCount(
        this.UtilityService.stringifyObjectsInQueryObject(
          archivedContractsQuery
        )
      )
    ]).subscribe({
      next: ([
        draftContractsCount,
        activeContractsCount,
        renewedContractsCount,
        expiredContractsCount,
        archivedContractsCount
      ]) => {
        this.contractCounts.set('Draft Contracts', {
          count: Number(draftContractsCount),
          FilterModel: {
            status: {
              filterType: 'set',
              values: ['NEW']
            },
            isFinalized: {
              filterType: 'set',
              values: ['0']
            }
          },
          route: contractsRoute
        });
        this.contractCounts.set('Active Contracts', {
          count: Number(activeContractsCount),
          FilterModel: {
            status: {
              filterType: 'set',
              values: [
                'NEW',
                'EXPIRES30',
                'EXPIRES90',
                'RENEW30',
                'RENEW90',
                'REVIEW'
              ]
            },
            isFinalized: {
              filterType: 'set',
              values: ['1']
            }
          },
          route: contractsRoute
        });
        this.contractCounts.set('Renewed Contracts', {
          count: Number(renewedContractsCount),
          FilterModel: {
            status: {
              filterType: 'set',
              values: ['RENEWED']
            }
          },
          route: contractsRoute
        });
        this.contractCounts.set('Expired Contracts', {
          count: Number(expiredContractsCount),
          FilterModel: {
            status: {
              filterType: 'set',
              values: ['EXPIRED']
            }
          },
          route: contractsRoute
        });
        this.contractCounts.set('Archived Contracts', {
          count: Number(archivedContractsCount),
          FilterModel: {
            status: {
              filterType: 'set',
              values: ['ARCHIVED']
            }
          },
          route: contractsRoute
        });

        const totals =
          draftContractsCount +
          activeContractsCount +
          renewedContractsCount +
          expiredContractsCount +
          archivedContractsCount;

        this.contractCounts.set('Total Contracts', totals);
        this._cdr.markForCheck();
      }
    });
  }

  ngOnDestroy(): void {
    this.SharedDataServiceSubscription?.unsubscribe();
  }

  ngOnInit(): void {
    if (!this.CompanyId) {
      throw this.ToastService.addErrorToast('Error', 'Missing Client Id.', {
        returnError: true
      });
    }

    this.SharedDataServiceSubscription =
      this.SharedDataService.listenForEvent<User>().subscribe({
        next: ({ eventName, data }) => {
          if (eventName === 'SetLoggedInUser' && data) {
            if (!data) {
              throw this.ToastService.addErrorToast(
                'Error',
                'There was an error retrieving user information.',
                { returnError: true }
              );
            }

            const { UserAccountRole } = data;

            if (!UserAccountRole) {
              throw this.ToastService.addErrorToast(
                'Error',
                'User is missing permission information.',
                { returnError: true }
              );
            }

            const { role } = UserAccountRole;

            if (!role) {
              throw this.ToastService.addErrorToast(
                'Error',
                'User is missing permission.',
                { returnError: true }
              );
            }

            this.userAccountRole = role;

            if (this.userAccountRole === 'CLIENT_ADMIN') {
              this.loadCompanyContracts(this.CompanyId);
            }
            this._cdr.markForCheck();
          }
        }
      });

    this.SharedDataService.sendEvent('GetLoggedInUser');
  }
}
